import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import { ISurfaceAssistEngineToken, type ISurfaceAssistEngine } from './assist/SurfaceAssistEngine';
import { runTileChart, tileChartKey } from './TileChartQuery';
import { assistFieldLabel, metricLabel } from '@/shared/analytics/MetricFormatting';
import type { AdHocAggregateResult, AdHocTrendResult, AnalyticsField, AnalyticsFilter, AnalyticsMetric, TimeBucket } from '@/shared/analytics/AnalyticsTypes';
import type { PinnedTile, TileSpec } from './PinnedTileTypes';
import type { AssistContext, AssistVizKind } from '@/shared/assist/AssistContract';

function defaultTitle(viz: AssistVizKind, primaryField: AnalyticsField, metric: AnalyticsMetric): string {
    if (viz === 'trend') return `${metricLabel(metric)} over time`;
    return `${metricLabel(metric)} by ${assistFieldLabel(primaryField).toLowerCase()}`;
}

/** Filler primary field a trend tile never reads but TileSpec still requires; non-trend tiles always supply a real one. */
const TREND_PRIMARY_FIELD_FILLER: AnalyticsField = 'user_department';

/**
 * Drives the "Add insight" modal: the visualization config (viz / field /
 * metric / bucket), a live preview query, and an AI "describe-to-configure" path
 * that reuses the assist engine to turn a sentence into the same chart config.
 * It does not own persistence — the section saves `currentSpec()` to the
 * {@link PinnedTilesStore}, so an AMA pin and a modal save converge on one shape.
 */
@injectable()
export class AddTileViewModel extends BaseViewModel {
    modalOpen = false;
    /** The id of the tile being edited, or null when building a new tile. */
    editingTileId: string | null = null;
    title = '';
    /** Null until the user (or describe-to-configure) picks one — a fresh tile starts unconfigured. */
    viz: AssistVizKind | null = null;
    primaryField: AnalyticsField | null = null;
    metric: AnalyticsMetric | null = null;
    bucket: TimeBucket = 'week';
    limit = 10;

    describePrompt = '';
    describing = false;
    describeError: string | null = null;

    previewLoading = false;
    previewError: string | null = null;
    aggregate: AdHocAggregateResult | null = null;
    trend: AdHocTrendResult | null = null;

    private previewKey: string | null = null;

    constructor(
        @inject(IAnalyticsGatewayToken) private gateway: IAnalyticsGateway,
        @inject(ISurfaceAssistEngineToken) private engine: ISurfaceAssistEngine,
    ) {
        super();
    }

    /** Opens the modal for a fresh tile (resets the config to defaults). */
    openModal(): void {
        this.reset();
        this.modalOpen = true;
        this.notifyChange();
    }

    /** Opens the modal pre-filled to edit an existing tile; saving updates that tile in place. */
    openForEdit(tile: PinnedTile): void {
        this.editingTileId = tile.id;
        this.title = tile.spec.title;
        this.viz = tile.spec.viz;
        this.primaryField = tile.spec.primaryField;
        this.metric = tile.spec.metric;
        this.bucket = tile.spec.bucket ?? 'week';
        this.limit = tile.spec.limit ?? 10;
        this.describePrompt = '';
        this.describeError = null;
        this.previewError = null;
        this.aggregate = null;
        this.trend = null;
        this.previewKey = null;
        this.modalOpen = true;
        this.notifyChange();
    }

    closeModal(): void {
        this.modalOpen = false;
        this.notifyChange();
    }

    /** Clears the config and preview to an unconfigured state (called on open of a fresh tile and after a save). */
    reset(): void {
        this.editingTileId = null;
        this.title = '';
        this.viz = null;
        this.primaryField = null;
        this.metric = null;
        this.bucket = 'week';
        this.limit = 10;
        this.describePrompt = '';
        this.describeError = null;
        this.previewError = null;
        this.aggregate = null;
        this.trend = null;
        this.previewKey = null;
        this.notifyChange();
    }

    setTitle(value: string): void {
        this.title = value;
        this.notifyChange();
    }

    setViz(value: AssistVizKind): void {
        this.viz = value;
        this.notifyChange();
    }

    setPrimaryField(value: AnalyticsField): void {
        this.primaryField = value;
        this.notifyChange();
    }

    setMetric(value: AnalyticsMetric): void {
        this.metric = value;
        this.notifyChange();
    }

    setBucket(value: TimeBucket): void {
        this.bucket = value;
        this.notifyChange();
    }

    setLimit(value: number): void {
        this.limit = value;
        this.notifyChange();
    }

    setDescribePrompt(value: string): void {
        this.describePrompt = value;
        this.notifyChange();
    }

    /** Whether enough has been picked to build a spec / render a preview. */
    get isComplete(): boolean {
        if (this.viz === null || this.metric === null) return false;
        return this.viz === 'trend' || this.primaryField !== null;
    }

    /** The tile spec for the current config, or null while still unconfigured; the title falls back to a generated one. */
    currentSpec(): TileSpec | null {
        const viz = this.viz;
        const metric = this.metric;
        if (viz === null || metric === null) return null;
        if (viz !== 'trend' && this.primaryField === null) return null;
        const primaryField = this.primaryField ?? TREND_PRIMARY_FIELD_FILLER;
        const title = this.title.trim().length > 0 ? this.title.trim() : defaultTitle(viz, primaryField, metric);
        return {
            title,
            viz,
            primaryField,
            metric,
            bucket: viz === 'trend' ? this.bucket : undefined,
            limit: viz === 'trend' ? undefined : this.limit,
        };
    }

    /** Runs the preview query for the current config; dedupes identical runs. Clears the preview while unconfigured. */
    async runPreview(pageFilters: AnalyticsFilter[]): Promise<void> {
        const spec = this.currentSpec();
        if (spec === null) {
            this.aggregate = null;
            this.trend = null;
            this.previewKey = null;
            this.previewLoading = false;
            this.notifyChange();
            return;
        }
        const key = tileChartKey(spec, pageFilters);
        if (this.previewKey === key && this.previewError === null) return;
        this.previewKey = key;
        this.previewLoading = true;
        this.previewError = null;
        this.aggregate = null;
        this.trend = null;
        this.notifyChange();
        try {
            const data = await runTileChart(this.gateway, spec, pageFilters);
            if (this.previewKey !== key) return;
            this.aggregate = data.aggregate;
            this.trend = data.trend;
        } catch (err) {
            if (this.previewKey !== key) return;
            this.previewError = err instanceof Error ? err.message : String(err);
        } finally {
            if (this.previewKey === key) {
                this.previewLoading = false;
                this.notifyChange();
            }
        }
    }

    /** Turns a natural-language description into the chart config via the assist engine. */
    async describe(context: AssistContext): Promise<void> {
        const prompt = this.describePrompt.trim();
        if (prompt.length === 0 || this.describing) return;
        this.describing = true;
        this.describeError = null;
        this.notifyChange();
        try {
            const plan = await this.engine.interpret({ prompt: `Show a chart of: ${prompt}`, context });
            if (plan.workflow === 'chart') {
                this.viz = plan.viz;
                this.primaryField = plan.primaryField;
                this.metric = plan.metric;
                if (plan.bucket !== undefined) this.bucket = plan.bucket;
                if (plan.title.length > 0) this.title = plan.title;
                this.previewKey = null;
            } else {
                this.describeError = 'That didn’t map to a chart. Try a metric broken down by a field — e.g. “total tokens by department”.';
            }
        } catch (err) {
            this.describeError = err instanceof Error ? err.message : String(err);
        } finally {
            this.describing = false;
            this.notifyChange();
        }
    }
}
